import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { LTButton, LTText } from '@lambdatestincprivate/lt-components';
import { handleOAuthReturn, stripOAuthParams } from '../../app/gh-auth';
import styles from './states.module.css';

// Module-level once guard: the OAuth return must be processed EXACTLY once
// per page load. React 18 StrictMode (and any remount) double-invokes the
// effect; the second run would find the stash already consumed, fall to the
// anomaly path, and reload — clobbering the first run's redirect. A module
// singleton survives the double-invoke and is reset only by the full
// navigation this component always ends in.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var oauthHandled = false;

/**
 * Rendered by the standalone app shell when the URL carries GitHub OAuth
 * return params (?code&state / ?error&state). All logic lives in
 * handleOAuthReturn; this component only spins, redirects, or offers a
 * retry. Host mounts (kaneai) never render this.
 */
export function GhOAuthReturn(_ref) {
  var params = _ref.params;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    failed = _useState2[0],
    setFailed = _useState2[1];
  useEffect(function () {
    // No `alive` cleanup guard: the once-flag already ensures a single run,
    // and every outcome ends in a full navigation (replace/reload) or a
    // setFailed on the still-mounted instance — a stale-closure bail would
    // instead strand the spinner under StrictMode's mount/cleanup/mount.
    if (oauthHandled) return;
    oauthHandled = true;
    void handleOAuthReturn(params).then(function (outcome) {
      if (outcome.kind === 'redirect') {
        window.location.replace(outcome.url);
        return;
      }
      if (outcome.kind === 'anomaly') {
        // Nonce mismatch / bookmarked callback: drop the OAuth params and
        // reload clean — the viewer boots normally.
        stripOAuthParams();
        window.location.reload();
        return;
      }
      setFailed({
        retryUrl: outcome.retryUrl
      });
    });
  }, [params]);
  if (failed) {
    return /*#__PURE__*/_jsx("div", {
      className: "ltEvidenceViewer ".concat(styles.wrap),
      role: "alert",
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.card,
        children: [/*#__PURE__*/_jsx(LTText, {
          as: "h1",
          text: "GitHub sign-in didn't complete",
          color: "var(--lt-text-primary)",
          className: styles.heading,
          style: {
            fontSize: '20px',
            lineHeight: '28px',
            fontWeight: 600
          }
        }), /*#__PURE__*/_jsx(LTText, {
          as: "p",
          text: "The sign-in code couldn't be exchanged (it may have expired \u2014 they're single-use and short-lived). Trying again mints a fresh one.",
          variant: LTText.TextVariant.SUBHEADER_REGULAR,
          color: "var(--lt-text-secondary)",
          className: styles.body
        }), /*#__PURE__*/_jsx(LTButton, {
          label: "Try again",
          variant: LTButton.Variant.PRIMARY,
          size: LTButton.Size.MEDIUM,
          onClick: function onClick() {
            if (failed.retryUrl) {
              window.location.replace(failed.retryUrl);
            } else {
              stripOAuthParams();
              window.location.reload();
            }
          }
        })]
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    className: "ltEvidenceViewer ".concat(styles.wrap),
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.card,
      "aria-busy": "true",
      children: [/*#__PURE__*/_jsx(LTText, {
        as: "h1",
        text: "Connecting GitHub\u2026",
        color: "var(--lt-text-primary)",
        className: styles.heading,
        style: {
          fontSize: '20px',
          lineHeight: '28px',
          fontWeight: 600
        }
      }), /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: "Finishing sign-in and returning you to the evidence pack.",
        variant: LTText.TextVariant.SUBHEADER_REGULAR,
        color: "var(--lt-text-secondary)",
        className: styles.body
      })]
    })
  });
}